import { CommonModule } from "@angular/common";
import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatTooltipModule } from "@angular/material/tooltip";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ExpandableAccordionComponent } from "../../common/expandable-accordion/expandable-accordion.component";
import { KnowledgeGraphModalComponent } from "../../modal-templates/knowledge-graph-modal/knowledge-graph-modal.component";
import { GenerateTestCasesModalComponent } from "../../modal-templates/generate-test-cases-modal/generate-test-cases-modal.component";
import { RciQualityReportModalComponent } from "../../modal-templates/rci-quality-report-modal/rci-quality-report-modal.component";
import { NoDataComponent } from "../../common/no-data/no-data.component";
import { MorphBoxLoaderComponent } from "../../common/morph-box-loader/morph-box-loader.component";
import { RestService } from "../../../core/services/rest.service";

export interface UserStory {
  storyId: string;
  summary: string;
  rciScore?: number;
  description?: string;
  acceptanceCriteria?: string[];
  qualityReportId?: string;
  [key: string]: any;
}

export interface ProcessingCounts {
  identified: number;
  newCount: number;
  extractionCompleted: number;
  extractionPending: number;
  rciGenerated: number;
  kgGenerated: number;
}

const USER_STORY_COLUMNS = [
  { mapping_key: "storyId",           display_name: "Story ID",            isVisible: true  },
  { mapping_key: "summary",           display_name: "Summary",             isVisible: true  },
  { mapping_key: "description",       display_name: "Description",         isVisible: false },
  { mapping_key: "acceptanceCriteria",display_name: "Acceptance Criteria", isVisible: false },
  { mapping_key: "qualityReportId",   display_name: "Quality Report",      isVisible: false },
];

const EMPTY_COUNTS: ProcessingCounts = {
  identified: 0,
  newCount: 0,
  extractionCompleted: 0,
  extractionPending: 0,
  rciGenerated: 0,
  kgGenerated: 0,
};

const DEFAULT_EXTRACT_MESSAGE = "Extracting user stories…";
const LUMOS_WAIT_MESSAGE = "Awaiting initial batch from LUMOS platform...";
const LUMOS_WAIT_MS = 40_000;

@Component({
  selector: "app-tcg-user-stories",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTooltipModule,
    ExpandableAccordionComponent,
    RciQualityReportModalComponent,
    NoDataComponent,
    MorphBoxLoaderComponent,
  ],
  templateUrl: "./tcg-user-stories.component.html",
  styleUrl: "./tcg-user-stories.component.scss",
})
export class TcgUserStoriesComponent implements OnChanges, OnDestroy {
  private readonly modalService = inject(NgbModal);
  private readonly restService = inject(RestService);

  @ViewChild(ExpandableAccordionComponent) private accordionRef?: ExpandableAccordionComponent;

  @Input() stories: UserStory[] = [];
  @Input() projectId = "";
  @Input() configId = "";
  /** Counts from parent progress polling — drives counts bar values. */
  @Input() processingCounts: Partial<ProcessingCounts> | null = null;
  /** True while extraction API is polled; locks action buttons until COMPLETED. */
  @Input() isProcessing = false;
  /**
   * When true, shows counts bar (and hides action buttons).
   * Parent sets false once Identified === Extraction Completed.
   */
  @Input() showCountsBar = false;
  /** Server-side total number of stories (across all pages). */
  @Input() serverTotalElements = 0;
  /** Current zero-based page index from server. */
  @Input() serverPageIndex = 0;
  /** Total number of pages from server. */
  @Input() serverTotalPages = 0;
  /** Page size (matches server page size). */
  @Input() serverPageSize = 10;

  @Output() generateTestCases  = new EventEmitter<any[]>();
  @Output() viewKnowledgeGraph = new EventEmitter<void>();
  /** Emitted when user clicks prev/next — carries the requested zero-based page index. */
  @Output() pageChange = new EventEmitter<number>();

  accordionData: { columns: any[]; rows: UserStory[] } = { columns: USER_STORY_COLUMNS, rows: [] };

  selectedStories: UserStory[] = [];
  isSelectAllChecked = false;
  /**
   * True when the user checked "Select all" — subsequent page loads will
   * auto-select every story on the new page.
   */
  isSelectAllPages = false;
  /**
   * Set true during page navigation to suppress the false "deselect all" signal
   * that the accordion emits when its data switches to a new page (no matching
   * IDs → empty selection emitted before our setTimeout auto-select fires).
   */
  private isNavigating = false;
  /** Accumulated stories selected across all pages while isSelectAllPages is active. */
  private allPagesSelectedIds = new Set<string>();
  private allPagesSelectedStories: UserStory[] = [];
  /**
   * Persists individually-selected stories across page navigation.
   * Keyed by storyId so selections survive prevPage / nextPage round-trips
   * without requiring "Select all pages" mode.
   */
  private crossPageSelectedMap = new Map<string, UserStory>();
  counts: ProcessingCounts = { ...EMPTY_COUNTS };
  loaderMessage = DEFAULT_EXTRACT_MESSAGE;
  /** True when Manage Assets → RCI (Requirement Analyzer) is enabled. */
  isRciEnabled = false;

  private rciStatusResolved = false;
  private lumosWaitTimer: ReturnType<typeof setTimeout> | null = null;

  get hasSelection(): boolean {
    return this.selectedStories.length > 0 || this.allPagesSelectedStories.length > 0;
  }

  /**
   * Returns the full selection across all pages.
   * When Select All Pages mode is active this merges accumulated selections from
   * previously visited pages with whatever is selected on the current page.
   */
  get allSelectedStories(): UserStory[] {
    if (!this.isSelectAllPages || this.allPagesSelectedStories.length === 0) {
      return this.selectedStories;
    }
    const currentIds = new Set(this.selectedStories.map(s => String(s.storyId ?? '')));
    const merged = [...this.selectedStories];
    for (const s of this.allPagesSelectedStories) {
      const id = String(s.storyId ?? '');
      if (id && !currentIds.has(id)) merged.push(s);
    }
    return merged;
  }

  /** Actions stay locked until extraction polling finishes (status COMPLETED). */
  get canUseActions(): boolean {
    return !this.isProcessing && this.hasSelection;
  }

  /** Shown while action buttons are waiting for COMPLETED. */
  readonly kgWaitingTooltip =
    "Knowledge graph is still generating. This action will be available once extraction is complete.";

  readonly generateWaitingTooltip =
    "Knowledge graph is still generating. Test case generation will be available once extraction is complete.";

  /** Use server counts when available, fall back to local stories length. */
  get totalItems(): number { return this.serverTotalElements || this.stories.length; }
  get pageSize(): number   { return this.serverPageSize; }
  get pageIndex(): number  { return this.serverPageIndex; }
  get totalPages(): number { return this.serverTotalPages || Math.ceil(this.totalItems / this.pageSize); }
  get startItem(): number  { return this.totalItems === 0 ? 0 : this.pageIndex * this.pageSize + 1; }
  get endItem(): number    { return Math.min((this.pageIndex + 1) * this.pageSize, this.totalItems); }

  /** Disable story selection while any extraction is still pending. */
  get isSelectionDisabled(): boolean {
    return this.counts.extractionPending > 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["stories"]) {
      // Stories input is already the correct server page — render directly.
      this.resyncSelectedStories();
      this.accordionData = { columns: USER_STORY_COLUMNS, rows: this.stories };

      // When "Select all pages" mode is active, auto-select every row on the new page.
      if (this.isSelectAllPages && this.stories.length > 0) {
        setTimeout(() => {
          if (!this.accordionRef) return;
          this.isSelectAllChecked = true;
          this.accordionRef.isSelectAllChecked = true;
          this.accordionRef.toggleSelectAll();
          // Accumulate newly loaded page stories into cross-page selection
          this.mergeCurrentPageIntoAllPages();
          // Navigation is complete — resume detecting manual deselects.
          this.isNavigating = false;
        });
      } else {
        // Restore individual cross-page selections in the accordion after its
        // own ngOnChanges has run (data swap clears accordion's selectedRows).
        setTimeout(() => {
          this.restoreAccordionSelections();
          this.isNavigating = false;
        });
      }
    }
    if (changes["processingCounts"] || changes["isProcessing"]) {
      this.counts = { ...EMPTY_COUNTS, ...(this.processingCounts ?? {}) };
      if (this.isSelectionDisabled) {
        this.clearSelection();
      }
    }
    if (changes["isProcessing"] || changes["stories"] || changes["serverTotalElements"]) {
      this.syncLumosWaitMessage();
    }
    if (changes["projectId"] || !this.rciStatusResolved) {
      this.resolveRciEnabled();
    }
  }

  ngOnDestroy(): void {
    this.clearLumosWaitTimer();
  }

  /**
   * When RCI (Requirement Analyzer) is enabled in Manage Assets and extraction
   * is still waiting for the first batch, switch the loader copy after 1 minute.
   */
  private syncLumosWaitMessage(): void {
    const waitingForFirstBatch = this.isProcessing && this.totalItems === 0;

    if (!waitingForFirstBatch) {
      this.clearLumosWaitTimer();
      this.loaderMessage = DEFAULT_EXTRACT_MESSAGE;
      return;
    }

    this.resolveRciEnabled(() => {
      if (!this.isProcessing || this.totalItems > 0) return;
      if (!this.isRciEnabled) {
        this.loaderMessage = DEFAULT_EXTRACT_MESSAGE;
        return;
      }
      if (this.lumosWaitTimer || this.loaderMessage === LUMOS_WAIT_MESSAGE) return;
      this.lumosWaitTimer = setTimeout(() => {
        this.lumosWaitTimer = null;
        if (this.isProcessing && this.totalItems === 0 && this.isRciEnabled) {
          this.loaderMessage = LUMOS_WAIT_MESSAGE;
        }
      }, LUMOS_WAIT_MS);
    });
  }

  private resolveRciEnabled(done?: () => void): void {
    if (this.rciStatusResolved) {
      done?.();
      return;
    }
    if (!this.projectId) {
      done?.();
      return;
    }

    this.restService.getApplicationDetails(this.projectId).subscribe({
      next: (res: any) => {
        const assets: any[] = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        this.isRciEnabled = assets.some((asset) => this.isRequirementAnalyzerAsset(asset) && !!asset.alreadyMapped);
        this.rciStatusResolved = true;
        done?.();
      },
      error: () => {
        this.isRciEnabled = false;
        this.rciStatusResolved = true;
        done?.();
      },
    });
  }

  private isRequirementAnalyzerAsset(asset: any): boolean {
    const name = String(asset?.name ?? "").toLowerCase();
    const displayName = String(asset?.displayName ?? "").toLowerCase();
    const haystack = `${name} ${displayName}`;
    return (
      haystack.includes("requirement analyzer") ||
      haystack.includes("requirementanalyzer") ||
      name === "rci" ||
      /\brci\b/.test(haystack)
    );
  }

  private clearLumosWaitTimer(): void {
    if (this.lumosWaitTimer) {
      clearTimeout(this.lumosWaitTimer);
      this.lumosWaitTimer = null;
    }
  }

  /** Keep selectedStories pointing at current row objects after a page change. */
  private resyncSelectedStories(): void {
    // When select-all-pages mode is on, the new page will be fully auto-selected
    // via setTimeout in ngOnChanges — skip any partial re-sync here.
    if (this.isSelectAllPages) return;

    // Restore from the cross-page map — picks up any story on this page that
    // the user had previously checked (even on a different page visit).
    this.selectedStories = this.stories.filter(s => {
      const id = String(s.storyId ?? '');
      return id && this.crossPageSelectedMap.has(id);
    });
  }

  /**
   * After the accordion's own ngOnChanges has run (which clears its selectedRows
   * because the page data swapped), re-stamp the correct checked indices so the
   * checkboxes reflect the cross-page selection state.
   */
  private restoreAccordionSelections(): void {
    if (!this.accordionRef || this.isSelectAllPages) return;

    const indices: number[] = [];
    this.stories.forEach((s, i) => {
      const id = String(s.storyId ?? '');
      if (id && this.crossPageSelectedMap.has(id)) indices.push(i);
    });

    if (indices.length === 0) return;

    this.accordionRef.selectedRows.clear();
    indices.forEach(i => this.accordionRef!.selectedRows.add(i));
    this.accordionRef.isSelectAllChecked = indices.length === this.stories.length;
    // Fire emitSelection so onAccordionAction updates selectedStories / toolbar checkbox.
    this.accordionRef.emitSelection();
  }

  /** Merge stories currently visible on this page into the cross-page accumulator. */
  private mergeCurrentPageIntoAllPages(): void {
    for (const story of this.stories) {
      const id = String(story.storyId ?? '');
      if (id && !this.allPagesSelectedIds.has(id)) {
        this.allPagesSelectedIds.add(id);
        this.allPagesSelectedStories.push(story);
      }
    }
  }

  prevPage(): void {
    if (this.pageIndex <= 0) return;
    this.isNavigating = true;
    this.persistCurrentPageSelections();
    if (this.isSelectAllPages) {
      this.mergeCurrentPageIntoAllPages();
    }
    this.pageChange.emit(this.pageIndex - 1);
  }

  nextPage(): void {
    if (this.pageIndex >= this.totalPages - 1) return;
    this.isNavigating = true;
    this.persistCurrentPageSelections();
    if (this.isSelectAllPages) {
      this.mergeCurrentPageIntoAllPages();
    }
    this.pageChange.emit(this.pageIndex + 1);
  }

  /**
   * Snapshot the current page's checkbox state into crossPageSelectedMap so
   * it can be restored when the user navigates back.
   * Stories on this page that are NOT checked are removed from the map so a
   * previous selection can be cleared by unchecking and then navigating away.
   */
  private persistCurrentPageSelections(): void {
    const selectedIds = new Set(
      this.selectedStories.map(s => String(s.storyId ?? '')).filter(Boolean)
    );
    for (const story of this.stories) {
      const id = String(story.storyId ?? '');
      if (!id) continue;
      if (selectedIds.has(id)) {
        this.crossPageSelectedMap.set(id, story);
      } else {
        this.crossPageSelectedMap.delete(id);
      }
    }
  }

  /** Called when the toolbar "Select All" checkbox changes. Delegates to the accordion. */
  toggleSelectAll(): void {
    if (this.isSelectionDisabled || !this.accordionRef) return;
    if (this.isSelectAllChecked) {
      // User just checked "Select all" — enter select-all-pages mode.
      this.isSelectAllPages = true;
      this.allPagesSelectedIds.clear();
      this.allPagesSelectedStories = [];
      // Seed accumulator with current page immediately.
      this.mergeCurrentPageIntoAllPages();
    } else {
      // User unchecked — exit select-all-pages mode and wipe every cross-page
      // selection so navigating to any page starts with a clean slate.
      this.isSelectAllPages = false;
      this.allPagesSelectedIds.clear();
      this.allPagesSelectedStories = [];
      this.crossPageSelectedMap.clear();
    }
    this.accordionRef.isSelectAllChecked = this.isSelectAllChecked;
    this.accordionRef.toggleSelectAll();
  }

  private clearSelection(): void {
    this.selectedStories = [];
    this.isSelectAllChecked = false;
    this.isSelectAllPages = false;
    this.isNavigating = false;
    this.allPagesSelectedIds.clear();
    this.allPagesSelectedStories = [];
    this.crossPageSelectedMap.clear();
    if (this.accordionRef) {
      this.accordionRef.isSelectAllChecked = false;
      this.accordionRef.selectedRows.clear();
      this.accordionRef.emitSelection();
    }
  }

  openGenerateModal(): void {
    if (!this.canUseActions) return;

    const stories = this.allSelectedStories;
    const ref = this.modalService.open(GenerateTestCasesModalComponent, {
      centered: true,
      size: "lg",
      backdrop: "static",
    });
    ref.componentInstance.configs = {
      title: "What type of test cases would you like to generate?",
      saveBtnText: "Generate",
      projectId: this.projectId,
      configId: this.configId,
      selectedStories: stories,
    };
    ref.result.then(
      (result) => {
        this.generateTestCases.emit({ stories, ...result });
      },
      () => {}
    );
  }

  openKnowledgeGraph(): void {
    if (!this.canUseActions) return;

    const storyKeys = this.allSelectedStories.map(s => s.storyId).filter(Boolean);

    const ref = this.modalService.open(KnowledgeGraphModalComponent, {
      size: 'xl',
      centered: true,
      windowClass: 'kg-modal-window',
    });
    ref.componentInstance.projectId = this.projectId;
    ref.componentInstance.configId = this.configId;
    ref.componentInstance.storyKeys = storyKeys;
    ref.componentInstance.selectedAll = storyKeys.length === this.stories.length;
    this.viewKnowledgeGraph.emit();
  }

  onAccordionAction(event: any): void {
    if (event?.type === "row_select") {
      this.selectedStories = event.data ?? [];
      // Keep toolbar "Select All" checkbox in sync with individual row selections
      const pageCount = this.accordionData.rows.length;
      this.isSelectAllChecked = pageCount > 0 && this.selectedStories.length === pageCount;
      // If the user manually deselects any row (not during a page transition),
      // exit select-all-pages mode so future navigation won't re-select everything.
      if (!this.isSelectAllChecked && this.isSelectAllPages && !this.isNavigating) {
        this.isSelectAllPages = false;
        this.allPagesSelectedIds.clear();
        this.allPagesSelectedStories = [];
        this.crossPageSelectedMap.clear();
      }
    }
    if (event?.type === "quality_report") {
      this.openQualityReport(event.data);
    }
  }

  openQualityReport(data: any): void {
    const ref = this.modalService.open(RciQualityReportModalComponent, {
      centered: true,
      size: "xl",
      backdrop: "static",
    });
    ref.componentInstance.configs = {
      projectId: this.projectId,
      configId: this.configId,
      reportId: data?.qualityReportId,
    };
  }
}
